import React, { useState, useEffect } from "react";
import { Field } from "redux-form";
import styled from "@emotion/styled";
import {
  accountNumber,
  routingNumber,
  accountType,
  validateAccountAndReEnterNumber
} from "@components/validation";
import { ACCOUNT_TYPES as accountTypes } from "@constants/achActionTypes";
import { FaCheck } from "react-icons/fa";
import RenderFormFieldNew from "./RenderFormFieldNew";
import RenderDropdownNew from "./RenderDropdownNew";
import BankAccountDisplay from "@components/BankAccountDisplay";
import { getSellBankAccountsWithAdded } from "@modules/Cart/selector";
import StyledRadioButton from "@components/forms/StyledRadioButton";
import { IoIosArrowBack } from "react-icons/io";
import { postNewPaymentMethod } from "@actions/paymentMethods";
import SmallButtonSpinner from "@components/SmallButtonSpinner";
import loadable from "@loadable/component";
const MessageContainer = loadable(() =>
  import(/* webpackChunkName: "MessageContainer" */ "../MessageContainer")
);
import { useDispatch, useSelector } from "react-redux";

const Container = styled.div`
  background-color: ${props => props.bgColor};
  box-shadow: ${props => props.boxShadow};
  border-radius: 10px;
  width: 100%;
  margin-top: 20px;
`;

const Header = styled.div`
  height: 96px;
  border-bottom: ${props => props.setBorderBottom};
  align-items: center;
  padding-left: 30px;
  padding-right: 30px;
  display: grid;
  grid-template-columns: auto auto 1fr;
  opacity: ${props => props.setOpacity};

  @media (max-width: 430px) {
    padding-left: 20px;
    padding-right: 20px;
  }
`;

const Circle = styled.div`
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background-color: ${props => props.bgColor};
  font-size: 14px;
  font-weight: bold;
  color: #12b2ea;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const HeaderText = styled.div`
  font-size: 18px;
  font-weight: 500;
  line-height: 2;
  color: #1f2021;
  margin-left: 20px;

  @media (max-width: 465px) {
    font-size: 16px;
  }
  @media (max-width: 400px) {
    font-size: 15px;
    margin-left: 10px;
  }
  @media (max-width: 335px) {
    font-size: 14px;
  }
  @media (max-width: 310px) {
    font-size: 13px;
  }
`;

const HeaderTextDesktop = styled(HeaderText)`
  @media (max-width: 385px) {
    display: none;
  }
`;

const HeaderTextMobile = styled.div`
  @media (min-width: 386px) {
    display: none;
  }

  font-weight: 500;
  line-height: 2;
  color: #1f2021;
  font-size: 15px;
  margin-left: 10px;

  @media (max-width: 335px) {
    font-size: 14px;
  }
  @media (max-width: 310px) {
    font-size: 13px;
  }
`;

const Edit = styled.div`
  font-size: 14px;
  font-weight: bold;
  text-align: right;
  justify-self: flex-end;
  color: #12b2ea;
  cursor: pointer;

  @media (max-width: 335px) {
    font-size: 13px;
  }
`;

const Body = styled.div`
  padding-left: 30px;
  padding-right: 30px;
  padding-bottom: 35px;
  padding-top: 35px;

  @media (max-width: 385px) {
    padding-left: 15px;
    padding-right: 15px;
  }
`;

const FieldContainer = styled.div`
  margin-bottom: 17px;
`;

const AccountContainer = styled.div`
  margin-bottom: 39px;
`;

const Button = styled.div`
  height: 60px;
  border-radius: 36px;
  font-size: 16px;
  font-weight: bold;
  margin: 0 auto;
  cursor: ${props => (props.valid ? "pointer" : "not-allowed")};
  box-shadow: ${props =>
    props.valid ? "0 9px 18px 0 rgba(57, 200, 118, 0.3)" : "none"};
  background-color: ${props => (props.valid ? "#39c876" : "#f2f2f3")};
  color: ${props => (props.valid ? "#ffffff" : "rgba(119, 121, 132, 0.5)")};
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 30px;

  &:hover {
    box-shadow: none;
    background-color: ${props => (props.valid ? "#39c876" : "#f2f2f3")};
  }
`;

const PaymentAccounts = styled.div`
  overflow-y: auto;
  max-height: 280px;
`;

const CardContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 100px;
  padding-right: 15px;
  margin-bottom: 5px;
  padding-top: 14px;
  padding-bottom: 12px;
  padding-left: 10px;
  margin-right: 5px;
  background-color: ${props =>
    props.isSelected ? "rgba(18, 178, 234, 0.05)" : ""};
  border-radius: 10px;

  &:hover {
    background-color: ${props =>
      props.isSelected
        ? "rgba(18, 178, 234, 0.05)"
        : "rgba(119, 121, 132, 0.05)"};
  }

  @media (max-width: 450px) {
    grid-template-columns: 1fr auto;
    grid-gap: 20px;
  }
  @media (max-width: 360px) {
    grid-gap: 5px;
  }
  @media (max-width: 340px) {
    grid-template-columns: 1fr;
    grid-gap: 0px;
  }
`;

const Card = styled.div`
  display: grid;
  grid-template-columns: 40px 1fr;
  align-items: center;
  font-size: 14px;
  line-height: 1.71;
  color: #777984;
  cursor: pointer;
`;

const AddCard = styled.div`
  cursor: pointer;
  width: fit-content;
  margin-top: 20px;
  font-size: 14px;
  font-weight: bold;
  line-height: 1.71;
  color: #12b2ea;
`;

const AddCardSpan = styled.span`
  color: rgba(18, 178, 234, 0.4);
`;

const Arrow = styled.div`
  display: flex;
  width: fit-content;
  align-items: center;
  cursor: pointer;
  margin-bottom: 20px;
`;

const EnterACH = ({
  openForm,
  missingInfo,
  errors,
  formContent,
  flowNumber,
  openToEdit,
  changeOpenForm,
  changeReduxField,
  achCompleted
}) => {
  const dispatch = useDispatch();
  const defaultSellBankAccount = useSelector(
    state => state.paymentMethods.defaultSellBankAccount
  );
  const bankAccounts = useSelector(state =>
    getSellBankAccountsWithAdded({
      paymentMethods: state.paymentMethods.sellPaymentMethods,
      defaultSellBankAccount: state.paymentMethods.defaultSellBankAccount
    })
  );

  const setOpacity = missingInfo && !openForm ? 0.4 : 1;
  const [submitAttempted, changeSubmitAttempted] = useState(false);
  const [openNewBankForm, setOpenNewBankForm] = useState(
    bankAccounts.allBankAccounts.length > 0 ? false : true
  );
  const [selectedId, changeSelectedId] = useState(
    formContent?.achRow ? formContent.achRow.paymentDetails.id : null
  );
  const [errorFromServer, setErrorFromServer] = useState(null);
  const [apiLoading, setApiLoading] = useState(false);
  const [errorClassName, setErrorClassName] = useState("animated fadeInDown");

  useEffect(() => {
    if (
      defaultSellBankAccount &&
      Object.keys(defaultSellBankAccount).length > 0
    ) {
      changeSelectedId(bankAccounts.defaultSelectedId);
      changeReduxField(
        "enterACH.achRow",
        bankAccounts.allBankAccounts.find(
          account =>
            account.paymentDetails.id === bankAccounts.defaultSelectedId
        )
      );
    }
  }, [defaultSellBankAccount]);

  const handleContinueClick = async () => {
    if (errors && Object.keys(errors).length) {
      return;
    }
    changeOpenForm(false);
  };

  const handleSaveAndContinueClick = async () => {
    setErrorClassName("animated fadeInDown");
    if (errors && Object.keys(errors).length) {
      return;
    }
    setApiLoading(true);
    const payObj = {
      paymentMethod: "ACH",
      default: false,
      type: "sell"
    };

    payObj.bankAccount = {
      routingNumber: formContent.routingNumber,
      accountNumber: formContent.accountNumber,
      accountType: formContent.accountType.toLowerCase(),
      save: false
    };

    const results = await dispatch(postNewPaymentMethod(payObj));
    if (results?.data?.data) {
      setOpenNewBankForm(false);
    }
    if (results.error) {
      setErrorFromServer(results.error);
    }
    setApiLoading(false);
  };
  return (
    <Container
      bgColor={!openForm ? "#f9f9f9" : "#ffffff"}
      boxShadow={!openForm ? "none" : "0 5px 20px 0 rgba(38, 38, 38, 0.05)"}
    >
      <Header
        setOpacity={setOpacity}
        setBorderBottom={
          !openForm && missingInfo
            ? "none"
            : "1px solid rgba(119, 121, 132, 0.15)"
        }
      >
        <Circle
          bgColor={
            !openForm ? "rgba(2, 163, 132, 0.1)" : "rgba(18, 178, 234, 0.1)"
          }
        >
          {!openForm && !errors && achCompleted() ? (
            <FaCheck
              style={{
                color: `rgba(2, 163, 132, 1)`,
                fontSize: "17px"
              }}
            />
          ) : (
            <>{flowNumber}</>
          )}
        </Circle>
        <HeaderTextDesktop>ACH Payment Information</HeaderTextDesktop>
        <HeaderTextMobile>ACH Payment Info.</HeaderTextMobile>
        {!missingInfo && !openForm && (
          <Edit
            onClick={() => {
              if (openToEdit) {
                changeOpenForm(true);
              }
            }}
          >
            Open
          </Edit>
        )}
      </Header>
      <div>
        {openForm ? (
          <Body>
            {openNewBankForm ? (
              <>
                <AccountContainer>
                  {bankAccounts.allBankAccounts.length > 0 && (
                    <Arrow
                      onClick={() => {
                        setOpenNewBankForm(false);
                      }}
                    >
                      <IoIosArrowBack
                        style={{
                          color: "rgba(119, 121, 132, 0.5)",
                          fontSize: "21px"
                        }}
                      />
                      <HeaderText>Back to Saved ACH</HeaderText>
                    </Arrow>
                  )}
                  {((submitAttempted && errors && Object.keys(errors).length) ||
                    errorFromServer) && (
                    <div className={errorClassName}>
                      <MessageContainer
                        message={
                          errorFromServer ||
                          "Please fill out all the fields in the form."
                        }
                        classes={"credit-card-form-error-message"}
                      />
                    </div>
                  )}
                  <FieldContainer>
                    <Field
                      name="accountNumber"
                      label="accountNumber"
                      component={RenderFormFieldNew}
                      type="text"
                      placeholder="Account Number"
                      validate={accountNumber}
                      paddingLeft={"30px"}
                      addIcon={
                        "https://cdn.cardcash.com/website/ui/elements/basic%20icons%20info.svg"
                      }
                      hoverImage={
                        "https://cdn.cardcash.com/website/ui/elements/ach_account.png"
                      }
                      imageContainerStyles={{
                        right: "16px",
                        width: "36px",
                        display: "flex",
                        justifyContent: "center",
                        height: "100%",
                        alignItems: "center"
                      }}
                    />
                  </FieldContainer>
                  <FieldContainer>
                    <Field
                      name="accountNumberConfirm"
                      label="accountNumberConfirm"
                      component={RenderFormFieldNew}
                      type="text"
                      placeholder="Re-enter Account Number"
                      validate={validateAccountAndReEnterNumber}
                      paddingLeft={"30px"}
                      addIcon={
                        "https://cdn.cardcash.com/website/ui/elements/basic%20icons%20info.svg"
                      }
                      imageContainerStyles={{
                        right: "16px",
                        width: "36px",
                        display: "flex",
                        justifyContent: "center",
                        height: "100%",
                        alignItems: "center"
                      }}
                    />
                  </FieldContainer>
                  <FieldContainer>
                    <Field
                      name="routingNumber"
                      label="routingNumber"
                      component={RenderFormFieldNew}
                      onChangeAction={() => {
                        setErrorClassName("animated fadeOutUp");
                        setErrorFromServer(null);
                      }}
                      type="text"
                      placeholder="Routing Number"
                      paddingLeft={"30px"}
                      validate={routingNumber}
                      addIcon={
                        "https://cdn.cardcash.com/website/ui/elements/basic%20icons%20info.svg"
                      }
                      hoverImage={
                        "https://cdn.cardcash.com/website/ui/elements/ach_routing.png"
                      }
                      imageContainerStyles={{
                        right: "16px",
                        width: "36px",
                        display: "flex",
                        justifyContent: "center",
                        height: "100%",
                        alignItems: "center"
                      }}
                    />
                  </FieldContainer>
                  <FieldContainer>
                    <Field
                      name="accountType"
                      label="accountType"
                      component={RenderDropdownNew}
                      type="text"
                      placeholder="Account Type"
                      list={accountTypes}
                      validate={accountType}
                      defaultValue={"Account Type"}
                      addArrow={true}
                    />
                  </FieldContainer>
                </AccountContainer>
                <Button
                  onClick={() => !apiLoading && handleSaveAndContinueClick()}
                  valid={errors && Object.keys(errors).length ? false : true}
                >
                  {apiLoading ? (
                    <SmallButtonSpinner img="https://cdn.cardcash.com/website/ui/elements/spinner-white.png" />
                  ) : (
                    "Save & Continue"
                  )}
                </Button>
              </>
            ) : (
              <>
                <PaymentAccounts>
                  {bankAccounts.allBankAccounts.map((item, index) => (
                    <CardContainer
                      key={index}
                      isSelected={item.paymentDetails.id === selectedId}
                    >
                      <Card
                        onClick={() => {
                          changeSelectedId(item.paymentDetails.id);
                          changeReduxField("enterACH.achRow", item);
                        }}
                      >
                        <StyledRadioButton
                          value={item}
                          checked={item.paymentDetails.id === selectedId}
                          onChange={f => f}
                          styles={{ marginTop: "3px" }}
                        />
                        <BankAccountDisplay
                          account_type={item.account_type}
                          account={item.account}
                          routing={item.routing}
                        />
                      </Card>
                    </CardContainer>
                  ))}
                </PaymentAccounts>
                <AddCard onClick={() => setOpenNewBankForm(true)}>
                  <AddCardSpan>+</AddCardSpan> Add a new bank account
                </AddCard>
                <Button
                  onClick={handleContinueClick}
                  valid={errors && Object.keys(errors).length ? false : true}
                >
                  Continue
                </Button>
              </>
            )}
          </Body>
        ) : (
          <>
            {achCompleted() ? (
              <Body>
                <BankAccountDisplay
                  account_type={formContent.achRow.account_type}
                  account={formContent.achRow.account}
                  routing={formContent.achRow.routing}
                />
              </Body>
            ) : null}
          </>
        )}
      </div>
    </Container>
  );
};

export default EnterACH;
